import type { SmileSimulationController } from "../hooks/useSmileSimulationController";
import { SmileSimulationLogo } from "./SmileSimulationLogo";

type SmileSimulationLoginPageProps = {
  auth: SmileSimulationController["auth"];
  onEmailChange: SmileSimulationController["actions"]["updateEmail"];
  onPasswordChange: SmileSimulationController["actions"]["updatePassword"];
  onSubmit: SmileSimulationController["actions"]["submitLogin"];
};

export function SmileSimulationLoginPage({
  auth,
  onEmailChange,
  onPasswordChange,
  onSubmit,
}: SmileSimulationLoginPageProps) {
  return (
    <section className="w-full">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <SmileSimulationLogo className="mx-auto h-auto w-[180px]" />

        <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-gray-900">
          Sign in to your account
        </h2>
        <p className="mt-3 text-center text-sm leading-6 text-gray-600">
          스마일 시뮬레이션을 시작하려면
          <br />
          이메일 또는 아이디와 비밀번호를 입력해 주세요.
        </p>
      </div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
        <form
          className="space-y-6"
          onSubmit={(event) => {
            event.preventDefault();
            void onSubmit();
          }}
        >
          <div>
            <label
              htmlFor="smile-simulation-email"
              className="block text-sm font-medium text-gray-900"
            >
              Email or username
            </label>
            <div className="mt-2">
              <input
                id="smile-simulation-email"
                name="email"
                type="text"
                autoComplete="username"
                autoFocus
                inputMode="email"
                value={auth.email}
                onChange={(event) => onEmailChange(event.target.value)}
                placeholder="your@email.com or username"
                aria-invalid={Boolean(auth.emailError)}
                aria-describedby={
                  auth.emailError ? "smile-simulation-email-error" : undefined
                }
                className="block h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-[#8F14E5] focus:outline-none focus:ring-2 focus:ring-[#8F14E5] sm:text-sm"
              />
            </div>
            {auth.emailError ? (
              <p
                id="smile-simulation-email-error"
                className="mt-2 text-sm font-medium text-red-600"
              >
                {auth.emailError}
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="smile-simulation-password"
              className="block text-sm font-medium text-gray-900"
            >
              Password
            </label>
            <div className="mt-2">
              <input
                id="smile-simulation-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={auth.password}
                onChange={(event) => onPasswordChange(event.target.value)}
                placeholder="비밀번호 입력"
                aria-invalid={Boolean(auth.passwordError)}
                aria-describedby={
                  auth.passwordError
                    ? "smile-simulation-password-error"
                    : undefined
                }
                className="block h-11 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder:text-gray-400 focus:border-[#8F14E5] focus:outline-none focus:ring-2 focus:ring-[#8F14E5] sm:text-sm"
              />
            </div>
            {auth.passwordError ? (
              <p
                id="smile-simulation-password-error"
                className="mt-2 text-sm font-medium text-red-600"
              >
                {auth.passwordError}
              </p>
            ) : null}
          </div>

          {auth.submitError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
              {auth.submitError}
            </p>
          ) : null}

          <div>
            <button
              type="submit"
              disabled={auth.isSubmitting}
              className="flex h-11 w-full items-center justify-center rounded-md bg-[#8F14E5] px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#7d10ca] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#8F14E5]"
            >
              {auth.isSubmitting ? "Signing in..." : "Sign in"}
            </button>
          </div>
        </form>

        <p className="mt-10 text-center text-sm text-gray-500">
          인증에 성공하면 바로 스마일 시뮬레이션을 시작할 수 있습니다.
        </p>
      </div>
    </section>
  );
}
